import { buildApiUrl, Type, sendPutRequest } from '../api'
import { type UpdateAssetSettings } from './schemas'
import { objectWithoutKeys, roundToFractionalCurrencyPrecision } from '@/helpers'
import { AssetType } from '@/features/assets/constants'
import type { DistributiveOmit } from '@/shared'

export async function saveAssetSettings(assetCode: string, settings: DistributiveOmit<UpdateAssetSettings, 'contacts'|'tariffProfile'>) {
  if (settings.assetType !== AssetType.Wheeling && settings.assetType !== AssetType.Solar) {
    throw new Error('Unexpected asset type when saving settings.')
  }

  const url = buildApiUrl(Type.AssetManagement, `asset/${assetCode}/settings`)
  const data = {
    ...settings,
    contractTerms: {
      ...objectWithoutKeys(settings.contractTerms, [
        'firstEscalationMonth',
        'firstEscalationYear',
        ...(
          settings.assetType === AssetType.Wheeling
            ? [
              'renewableEnergyCreditsIssuanceFeeInDollars',
              'renewableEnergyCreditsPlatformFeeInRands',
              'renewableEnergyCreditsTransferFeeInRands'
            ] satisfies (keyof typeof settings.contractTerms)[]
            : []
        )
      ]),
      firstEscalationMonth: settings.assetType != AssetType.Wheeling ?
        `${settings.contractTerms.firstEscalationYear}-${(settings.contractTerms.firstEscalationMonth ?? 0).toString().padStart(2, '0')}-01`
        : undefined,
      // Intentionally using `as ...` to ensure TS is aware of the object properties.
      renewableEnergyCreditsIssuanceFeeInCents: undefined as number|undefined,
      renewableEnergyCreditsTransferFeeInCents: undefined as number|undefined,
      renewableEnergyCreditsPlatformFeeInCents: undefined as number|undefined,
      renewableEnergyCreditsEffectiveDate: undefined as string|undefined
    },
    utilityProvider: {
      ...objectWithoutKeys(settings.utilityProvider, ['blendedTariff']),
      blendedTariffInCents: roundToFractionalCurrencyPrecision(settings.utilityProvider.blendedTariff * 100)
    }
  }

  if (settings.assetType === AssetType.Wheeling) {
    data.contractTerms.renewableEnergyCreditsEffectiveDate = settings.contractTerms.renewableEnergyCreditsEffectiveDate != null
      ? `${settings.contractTerms.renewableEnergyCreditsEffectiveDate.toISOString().split('T')[0]}`
      : undefined;

    data.contractTerms.renewableEnergyCreditsIssuanceFeeInCents = settings.contractTerms.renewableEnergyCreditsIssuanceFeeInDollars != null
      ? settings.contractTerms.renewableEnergyCreditsIssuanceFeeInDollars * 100
      : undefined;

    data.contractTerms.renewableEnergyCreditsTransferFeeInCents = settings.contractTerms.renewableEnergyCreditsTransferFeeInRands != null
      ? settings.contractTerms.renewableEnergyCreditsTransferFeeInRands * 100
      : undefined;

    data.contractTerms.renewableEnergyCreditsPlatformFeeInCents = settings.contractTerms.renewableEnergyCreditsPlatformFeeInRands != null
      ? settings.contractTerms.renewableEnergyCreditsPlatformFeeInRands * 100
      : undefined;
  }

  await sendPutRequest(url, { data })
}
